import type {
  DropsetDraft,
  MovementPattern,
  SetIntensity,
  SetWork,
  WeightSource,
  WorkoutEntryGroup,
} from '@jackedlogs/shared';

// Draft state for the mobile Logger. Mirrors the web's WorkoutLog
// draft surface, trimmed to what mobile can capture today:
//   - weighted + bodyweight set kinds (reps, weight or extra, per-set
//     unit kg/lb, tri-state pill: pending/done/failed)
//   - per-set note (500 chars) and RPE (1..10)
//   - superset / circuit grouping via WorkoutEntryGroup on each entry
//   - edit + repeat hydration (LoggerScreen.hydrateFromWorkout) — saves
//     can overwrite an existing session or clone its structure
//
// Still NOT modelled here (handled elsewhere or out-of-scope for the
// mobile logger surface): p1rm/loaded-carry/duration/side set kinds,
// dropsets, exercise-level notes/RPE.

// Per-set pill cycle: pending → done | pr → failed → pending.
// completedAt + fail + prFlash together encode the state — same shape
// the web's useSetCompletion hook uses, and what splits cleanly into
// the WorkoutSet payload at save time (prFlash is UI-only and is not
// persisted to the server). A failed set still keeps completedAt (it
// counted in volume, the lifter just missed the rep); returning to
// pending wipes all three.
export type SetState = 'pending' | 'done' | 'pr' | 'failed';

export type WeightUnit = 'kg' | 'lb';

export interface DraftSet {
  /** Free-text input — parsed to int on save. */
  reps: string;
  /** Free-text input — parsed to float on save. Display unit; converted
   *  to canonical kg via toCanonicalKg(weight, unit) on save. */
  weight: string;
  /** Per-set display unit; toggle independently. Default 'kg'. */
  unit: WeightUnit;
  /** Bodyweight-only sign for the EXTRA field. Android's decimal-pad
   *  doesn't expose a minus sign, so the negative path (band-assisted
   *  pull-ups, assisted-dip machine) needs an explicit toggle. The
   *  TextInput value stays positive; buildPayload multiplies by -1 at
   *  save time when this flag is set. Ignored on weighted entries. */
  extraNegative: boolean;
  /** ISO when the pill was first marked done; null while pending. */
  completedAt: string | null;
  /** True on the failed leg of the cycle; ignored when completedAt is null.
   *  A pure label (AP-0p model): `reps` already holds the clean reps done
   *  (the failed rep is decremented at fail time), so this changes no math. */
  fail: boolean;
  /** UI-only flag set at the pending→done transition when isSetPR
   *  returns true against the user's prior history. Drives the gold
   *  "PR" pill state in the SetRow. Not persisted to the API — the
   *  server recomputes records from scratch via /api/me/records, this
   *  is only the click-time celebratory cue. Defaults to false on
   *  every set-create path (emptySet / add_set / hydrate / pr-flash
   *  reset on failed/pending leg). Legacy persisted drafts without
   *  this field read as undefined and setStateOf treats them as
   *  plain 'done'. */
  prFlash: boolean;
  /** UI-only flag, twin of `prFlash` but for the DOWNWARD direction
   *  (next/coaching.md §9). Set at the pending→done transition when the
   *  set is a qualifying MAX-regression signal (RIR 0 / fail, reps ≤ 12,
   *  e1RM ≥ δ below a stale stored MAX) — drives the red ⬇️ marker in the
   *  timeline, mirror of the 🏆. Never persisted (the end-of-session
   *  prompt re-derives from the aggregate). Optional so the many
   *  set-create/hydration paths read it as undefined = false without a
   *  synchronised default bump (same pattern as `rir`/`weightSource`);
   *  cleared on the fail/unwind legs alongside prFlash. */
  regressionFlash?: boolean;
  /** Free-text note. Empty string = absent (omit-vs-zero convention).
   *  Stored on WorkoutSet.note at save. 500-char ceiling matches the
   *  schema; the textarea enforces it client-side too. */
  note: string;
  /** Per-set RPE 1..10 as a string (decimal-pad input). Empty = absent;
   *  parsed at save and dropped if not finite. */
  rpe: string;
  /** Per-set RIR (Reps in Reserve) 0..10 as a string. Empty = absent;
   *  parsed at save and dropped if not finite or out of range. RIR is
   *  the more objective fatigue indicator (RPE is perception-driven);
   *  the Progress Tier 2 fatigue-drift panel reads this. Optional on
   *  DraftSet so the in-flight Logger UI surfaces (planStart, hydrate,
   *  add_set, etc.) don't need a synchronised string-default bump on
   *  every new field — they read as undefined and the save path
   *  treats undefined exactly like the empty string. */
  rir?: string;
  /** Bilateral toggle stamped at set-create time (1.45.0). 1 =
   *  single-implement load (one DB row), 2 = paired implements
   *  (two-DB row). Drives volume math (entryTotals + buildSeries
   *  multiply by sides). Stored per-set so toggling the entry's
   *  default mid-workout doesn't retroactively shift the volume of
   *  already-completed sets. Falls back to 2 at read time when
   *  legacy/persisted drafts don't carry it, matching SL semantics. */
  loadedSides: 1 | 2;
  /** Cardio fields (1.66.9). Populated only on cardio entries; remain
   *  empty strings on weighted/bodyweight rows. Free-text inputs same
   *  as reps/weight; buildPayload parses + validates at save.
   *  - duration: total seconds. Required for cardio sets.
   *  - distanceM: metres (gym treadmills/bikes report km on the
   *    display; we ask the user to type metres for canonical storage).
   *  - avgHrBpm: average heart rate over the set.
   *  - calories: estimate from the machine display. */
  duration: string;
  distanceM: string;
  avgHrBpm: string;
  calories: string;
  /** PRESCRIPTION — "the plan asks for as many reps as possible here".
   *  Seeded by hydrateFromPlanStarter (planStart.ts -> the program slot's
   *  `amrapLastSet` / SetPrescription.amrap lifts onto the planned set).
   *  Drives the read-only AMRAP <Badge> next to the reps label so the
   *  athlete knows the prescribed `reps` is a floor to beat, not a
   *  ceiling. DISPLAY-ONLY and NEVER persisted: a prescription is what we
   *  PROPOSE, and what we propose must never cross the border into a
   *  measured value (next/measurement-contract.md §5). Persisting it
   *  would have every plan-prescribed set claim the athlete went to the
   *  limit — a measurement invented out of an intention. The OUTCOME is
   *  `amrap` below; the two never write each other. New sets via
   *  seedNextSet default to false: once the athlete goes off-plan past
   *  the prescribed last set, the AMRAP intent doesn't auto-carry. */
  amrapPrescribed: boolean;
  /** The prescribed rep FLOOR of an AMRAP set, as text, for display only.
   *  Exists because `reps` cannot hold it: on a prescribed AMRAP the reps
   *  cell starts EMPTY (LG-28). Seeding the floor into the value is what
   *  turned the prescription into the measurement — the athlete read the
   *  prefilled 8, did 8, and confirmed "to failure" on a set that never
   *  went there (Antenore's gym smoke, 2026-07-24, session 5e0d964d).
   *  The floor is still worth SHOWING — "at least 8" is the instruction —
   *  so it moves to the placeholder, where it cannot be submitted by
   *  doing nothing. Optional: absent on every set that isn't a prescribed
   *  AMRAP, and absent on drafts persisted before this field existed, so
   *  no draft migration is needed. Never persisted, same as
   *  `amrapPrescribed` (measurement-contract §5). */
  amrapFloor?: string;
  /** PRESCRIPTION — how the plan BOUNDS this set in time (HI-1,
   *  interval-training.md §9.1). Seeded by hydrateFromPlanStarter from
   *  SetPrescription.work; absent on every set no plan asked for.
   *  `countdown` ⇒ the work timer runs durationSec down and the bout
   *  closes itself at zero; `countup` ⇒ the athlete's tap closes it, as
   *  a reps set does today. DISPLAY/EXECUTION ONLY and NEVER persisted:
   *  the prescription proposes, the OUTCOME is `duration` above, which
   *  the clock earns (measurement-contract §5, same border amrapPrescribed
   *  and amrap keep). seedNextSet leaves it off — past the prescribed last
   *  set the athlete is off-plan, and the bout doesn't auto-carry. */
  workPrescribed?: SetWork;
  /** PRESCRIPTION — the effort the plan asked of THIS set, as canonical RIR
   *  (0..10, AP-0a: "mai due campi"). The athlete's scale is a render-time
   *  projection, never a second stored field.
   *
   *  ⭐ This field exists because effort was the LAST prescription still
   *  writing into the measurement it is supposed to propose. `rpe`/`rir`
   *  above are the OUTCOME; the plan used to seed them directly
   *  (planStarterHydration), so a set completed WITHOUT tapping the band —
   *  the Fail leg and the self-closing bout — saved the coach's target as
   *  the athlete's judgment. On a Fail that is not merely a wrong number: it
   *  records "I had three left" on a set the athlete could not finish.
   *  Proven by prescribedEffortLeak.test.ts before this field existed.
   *
   *  Same border `amrapPrescribed`/`amrap` and `workPrescribed`/`duration`
   *  already keep (measurement-contract §5): the prescription proposes, the
   *  outcome is earned. DISPLAY ONLY and NEVER persisted — buildPayload does
   *  not carry it. `seedNextSet` leaves it off: past the prescribed last set
   *  the athlete is off-plan and the target doesn't auto-carry.
   *
   *  Also set by a REPEAT (Antenore, 2026-08-25): re-running a session
   *  carries the previous effort as a TARGET, not as something performed
   *  again. A true EDIT leaves `rpe`/`rir` alone — there they ARE the
   *  measurement. */
  rirPrescribed?: number;
  /** PRESCRIPTION — WHAT DETERMINES THE LOAD on this set, as the program's
   *  own exclusive union (PE-22 K1). `absolute` kg · `percent-of-max` ·
   *  `history` ("use the athlete's last-logged weight") · `rpe`/`rir`
   *  (effort is the axis, the athlete picks the weight) · `none`.
   *
   *  ⭐ It exists because the axis had NOWHERE to land. Every other path
   *  into the draft carries a weight that is already RESOLVED — `weight`
   *  above is a number, and from "100 kg" there is no way back to knowing
   *  whether the coach wrote 100, or 80% of a max, or "whatever you lifted
   *  last time". `planStarterHydration` is lossy for exactly this reason
   *  (PlanStarter.sets carry `weightKg` and no kind), which is why the K1
   *  adapter could not be built on it.
   *
   *  ⛔ NOT `weightSource`. That field is the PROVENANCE of a weight that
   *  has already been resolved; this one is the axis still to resolve.
   *  Confusing the two is the obvious way to get K1 wrong: `weightSource:
   *  'history'` means "the number in the cell came from history", while
   *  `intensityPrescribed: {kind:'history'}` means "no number was written,
   *  go look it up".
   *
   *  ⛔ The axis is EXCLUSIVE — a set prescribed at 80% cannot also be
   *  prescribed at RIR 2, because the two contradict each other the moment
   *  the athlete finishes 80% at RIR 4, and neither would be right
   *  (program-editor-redesign.md, the K1 mockup). The union enforces it by
   *  construction. ⚠️ Orthogonal to how the set is BOUNDED (`workPrescribed`,
   *  `amrapPrescribed`): AMRAP says when you stop, not how much you load,
   *  which is why the two can coexist and effort-vs-weight cannot.
   *
   *  Same discipline as `workPrescribed`/`amrapPrescribed`/`rirPrescribed`:
   *  DISPLAY ONLY and NEVER persisted — buildPayload does not carry it, and
   *  the editor's writes go back through the composer's `updateCell`, never
   *  through a DraftSet. Optional, so drafts persisted before this field
   *  need no migration. */
  intensityPrescribed?: SetIntensity;
  /** PRESCRIPTION — the rest the plan wrote after THIS set, seconds
   *  (SetPrescription.restSec). Absent ⇒ the rest ladder decides, which is
   *  what every set does today; 0 is a real value ("straight into the next
   *  bout") and is honoured, so readers must test for undefined and not
   *  for truthiness. Never persisted: a rest is not a measurement. */
  restPrescribedSec?: number;
  /** PRESCRIPTION — the coach's per-set note (SetRowDraft.notesPerCell at
   *  this week), K3.4. Same discipline as the mirrors above: DISPLAY ONLY,
   *  never persisted (buildPayload does not carry it — it reads the program
   *  Draft, not a DraftSet), and absent means "nothing written", not "note
   *  cleared". The "Nota" chip's selected state reads this rather than
   *  re-deriving it, so the read half and the write half (the row's own
   *  `notesPerCell[weekIdx]`, via updateSetRowNote) can never disagree. */
  notePrescribed?: string;
  /** OUTCOME — "on THIS set I went to the limit on the open variable".
   *  Declared by the athlete, and ONLY by the athlete: the FocusPanel
   *  AMRAP chip is the single writer (update_set patch). Shares its
   *  nature with `fail` — an execution outcome, not structure — so it
   *  hydrates and persists exactly like it: buildPayload carries it onto
   *  the saved set (-> LogSet.amrap, omit-the-default: only `true`
   *  travels) and edit hydration reads it back, while repeat / the
   *  history copy blank it. Independent of `amrapPrescribed`: the athlete
   *  can mark a set no plan asked for, and can leave a prescribed-AMRAP
   *  set unmarked because they stopped short. */
  amrap: boolean;
  /** Provenance of the prefilled weight (Phase 1, default-weight chain).
   *  Stamped by hydrateFromPlanStarter via resolveDefaultWeight; flipped
   *  to 'manual' the first time the athlete edits the weight cell. Data-
   *  only — no UI cue. Forwarded into LogSet.weightSource at save. Absent
   *  on legacy/persisted drafts; absent = treat as 'manual' (those rows
   *  predate the field and were all athlete-typed). */
  weightSource?: WeightSource;
  /** Dropset chain attached to this set (1.80.0). null = no chain
   *  (default). The SetRow exposes an "Add dropset chain" affordance
   *  on weighted + bodyweight parents only; cardio rows live in
   *  CardioSetRow and skip this field entirely. Persisted into
   *  WorkoutSet.dropset at save via buildPayload's per-step parse +
   *  toCanonicalKg conversion (mirrors web's `builtDropset` in
   *  WorkoutLog.tsx:1112). Mirrors web SetDraft.dropset (line ~99). */
  dropset: DropsetDraft | null;
}

/** The identity of a draft ROW, for everything that has to say WHICH ROW:
 *  the logger cursor, the roster highlight, React keys, findIndex over
 *  entries (LG-39).
 *
 *  ⛔ Do not inline `entry.exerciseId` for this — that is the catalog FK, it
 *  is not unique, and using it here is the bug this helper exists to keep
 *  from coming back. Questions about WHICH EXERCISE (history, PR scope, rest
 *  defaults, display name) keep reading exerciseId directly.
 *
 *  The fallback covers plain DraftEntry values with no flat store behind them
 *  (test fixtures, a draft mid-hydration): they behave exactly as before. */
export const rowIdOf = (e: DraftEntry): string => e.entryId ?? e.exerciseId;

export const setStateOf = (s: DraftSet): SetState => {
  if (s.completedAt === null) return 'pending';
  if (s.fail) return 'failed';
  return s.prFlash ? 'pr' : 'done';
};

export interface DraftEntry {
  /** Stable identity of THIS row (LG-39). The flat store has minted one per
   *  entry since Phase 1 (ExerciseMeta.entryId) — this field only exposes it
   *  to the nested view the UI reads, via assembleDraftKeepIds.
   *
   *  ⛔ It exists because `exerciseId` is NOT unique: the same exercise can
   *  legitimately appear two or three times in one workout (walk to warm up,
   *  HIIT walking, walk to cool down — Antenore 2026-08-14). Anything that
   *  answers "WHICH ROW" — the cursor, the roster highlight, a React key,
   *  a findIndex over entries — keys on entryId. Anything that answers
   *  "WHICH EXERCISE" — history lookups, PR scope, rest defaults, catalog
   *  name resolution — keys on exerciseId and MUST keep doing so.
   *
   *  ⛔ Duplicate rows are NOT variants: three walks are three rows with no
   *  equipment variant, and their sets belong to the SAME historical series.
   *  Deducing a variant from a duplicate splits that series into fakes and
   *  nothing in the app shows it (equipment-model.md §10.2, §10.5).
   *
   *  Optional like every other late-added draft field: the many DraftEntry
   *  constructors (hydration, plan starter, test fixtures) build plain
   *  entries and flattenDraft mints the id for them. On the one runtime path
   *  the UI actually reads (useLoggerDraft → assembleDraftKeepIds) it is
   *  always present. */
  entryId?: string;
  /** Catalog slug — Exercise.slug (also stored as Exercise.id). NOT an
   *  identity: see entryId above. */
  exerciseId: string;
  /** Snapshot for display while the Logger is open; the API stamps
   *  the canonical name from the catalog at write time. */
  exerciseName: string;
  /** Catalog snapshot of the exercise's bodyweight flag. Drives the
   *  payload shape (weighted vs bodyweight) and the column header
   *  (KG vs EXTRA). Kept on the entry, not the set, since a single
   *  exercise picked into the workout always logs one way. */
  isBodyweight: boolean;
  /** Catalog snapshot of the exercise's movement pattern. Read by the
   *  post-save MAX-bump evaluation (rep-trust cap differs per pattern,
   *  e.g. squat tolerates higher reps than overhead press). Null when
   *  the catalog row has no pattern set or the entry was hydrated from
   *  a saved workout (edit mode) without a fresh catalog lookup; the
   *  MAX-bump path falls back to the default rep-trust cap then. */
  movementPattern: MovementPattern | null;
  /** Superset/circuit membership. Null = standalone entry. Two or more
   *  entries with the same `group.id` (and same `kind`) form a group;
   *  validation runs at buildPayload time so a half-set group never
   *  reaches the API. */
  group: WorkoutEntryGroup | null;
  /** Default bilateral mode for new sets in this entry (1.45.0). The
   *  entry-header chip (eligibility-gated by equipment kind) flips
   *  this; subsequent add_set actions stamp the value onto the new
   *  DraftSet. Already-completed sets keep their stamped value so a
   *  mid-entry flip doesn't rewrite history. Falls back to 2 (the
   *  SL-style default) for legacy hydrations. */
  loadedSides: 1 | 2;
  /** Cardio entry flag (1.66.9). True when the underlying catalog row
   *  is `movementPattern === 'cardio'`. Drives the SetRow variant
   *  (CardioSetRow vs the weighted SetRow), buildPayload branch, and
   *  the WorkoutSet kind emitted at save. Stamped at add_exercise
   *  time and at hydrateFromWorkout time so the entry knows its own
   *  kind regardless of the catalog snapshot in use. */
  isCardio: boolean;
  /** Time-based (isometric) entry flag — plank, wall sit, bird-dog hold,
   *  the physiotherapy isometrics (inbox 3dd20b1c). True when the catalog
   *  row is `isTimeBased`. Like isCardio it drives a duration input instead
   *  of reps/weight and emits kind 'timed' at save, but with no
   *  distance/HR/calories. Stamped at add_exercise + hydrateFromWorkout. */
  isTimeBased: boolean;
  /** Per-session RPE column toggle (Logger redesign slice 3, §6.3).
   *  Seeded from PlannedExerciseEntry.rpeEnabled (or
   *  Slot.rpeEnabled propagated by programResolution) via
   *  resolveRpeDefault on plan-start; otherwise false. Per-set RPE
   *  values live on DraftSet.rpe and aren't deleted when the column
   *  is hidden — re-toggling shows them back. Not persisted to the
   *  server (it's a session-level chrome flag); slice 3 keeps it
   *  on the draft so resume / repeat both see the user's last
   *  per-session choice. */
  /** Per-session visibility of the single effort column (effort chip).
   *  ON by default for weighted entries (opt-out per entry, decided
   *  2026-05-26); off for bodyweight/cardio. The scale (RIR | RPE) is
   *  the app-wide Profile.effortScale, not a per-entry choice. Per-set
   *  values live on DraftSet.rpe / DraftSet.rir and aren't deleted when
   *  the column is hidden. Not persisted to the server (session chrome);
   *  kept on the draft so resume / repeat see the user's last choice. */
  effortColumn: boolean;
  /** Per-session history-panel visibility (Logger redesign slice 4.1,
   *  §6 chip row §10.4). Hidden by default — the 🕐 chip toggles it
   *  open/closed. Per-session only; never persisted. */
  historyOpen: boolean;
  /** EQ-1 — WHICH physical machine this entry was logged on, as the bare
   *  slug (`machine-a`); the `equipment-instance:` namespace is applied at
   *  the write seam. Set at add (silently, only when the place knows
   *  exactly one) or at replace (the one place the athlete is ever asked).
   *  Null on every legacy hydration, which is what the whole history looks
   *  like — absence is the normal case, not a missing value. */
  equipmentInstance: string | null;
  /** EQ-1 — WHERE it was logged. The second axis: exercise x place is the
   *  list of special machines that place has. Sticky across entries; null
   *  until the athlete picks one. */
  location: string | null;
  sets: DraftSet[];
}

// A standalone timeline note event (next/timeline-logger.md §9, Solution C).
// The single chat-style composer appends one of these; it renders as a note
// mini-card interleaved in the timeline feed by performedAt. Distinct from the
// per-set DraftSet.note and the legacy DraftEntry.notes (both kept for read-
// compat, authoring retired in the timeline logger). Persisted at workout save
// as its own WorkoutNote DDB row — never on the set-aggregation path.
export interface NoteEventDraft {
  /** Client UUID, also the server id (offline-first idempotency). */
  id: string;
  /** Free text (4..2000, trimmed). The composer enforces the floor too. */
  text: string;
  /** ISO when sent. Live logging: wall clock. Past-session edit: an
   *  in-window tail time (timelinePlacement.placeCompletedSetAtTail), never
   *  the live clock. */
  performedAt: string;
  /** Catalog slug in focus when the note was sent, if any. Data-only — no UI
   *  surface; carried for a future feed ("note while on Back Squat"). */
  exerciseContextId?: string;
}

export interface LoggerDraft {
  /** ISO. Set on first mount. */
  startedAt: string;
  /** ISO. User-typed end time via the footer date editor (1.67.1).
   *  Absent during an active new/repeat workout — buildPayload then
   *  falls back to the latest per-set completedAt. Hydrated from
   *  `w.endedAt` in edit mode so the user sees the stored value and
   *  can correct it. */
  endedAt?: string;
  entries: DraftEntry[];
  /** Plan row this draft was started from. Carried into the save
   *  payload (NewWorkout.fromPlanId) so the server can flip the plan
   *  row to status=completed on success. Optional — direct/manual
   *  sessions don't have one. */
  fromPlanId?: string;
  /** Plan-named title forwarded into Workout.name on save. Lets the
   *  saved session keep the plan's identity in the history list. */
  name?: string;
  /** Plan-level notes forwarded into Workout.notes on save. */
  notes?: string;
  /** Read-only coach context for the workout-level "?" panel (program
   *  description + the day's session note). Resolved at Start from the
   *  program/plan template; display-only, NEVER written to the save payload
   *  (no denormalized snapshot — see [[feedback_no_denormalized_display_snapshots]]).
   *  Absent for ad-hoc/manual/edit sessions. */
  coachNotes?: { programDescription?: string; sessionNote?: string };
  /** Display-only plan/program reference for resolving the coach-notes "?"
   *  panel when the draft was NOT freshly started from a plan — i.e. a
   *  past session opened in Edit or Repeat mode (hydrateFromWorkout copies
   *  the saved Workout.fromPlanId here). useCoachNotes resolves notes from
   *  the cached programs/plans against it. Kept SEPARATE from `fromPlanId`
   *  on purpose: `fromPlanId` drives the save (plan-flip / program
   *  auto-advance), and a Repeat must not re-flip the source plan — so the
   *  reference used for display never reaches the payload. */
  coachNotesRef?: string;
  /** EQ-1 slice D — WHERE this session is happening, as the bare slug.
   *  Session-level because the place is a fact of the session: you pick it
   *  once, on arrival, and every entry created afterwards inherits it onto
   *  its own `location` (which is what actually reaches the set).
   *
   *  ⚠️ NOT a denormalised copy of the entries' value. This is the CURRENT
   *  CHOICE, live and changeable; each entry's `location` is the recorded
   *  fact of where that entry happened, and once written it stays put. They
   *  differ the moment you change gym mid-session — which is `EQ-1-SWITCH`,
   *  and the reason that voice is not free.
   *
   *  Optional so the many draft constructors need no synchronised bump;
   *  absent reads as "not said", exactly like every session before today. */
  location?: string | null;
  /** Standalone timeline note events (Solution C). Optional so the many
   *  LoggerDraft constructors (hydrate, plan-start, repeat, legacy persisted
   *  drafts) don't need a synchronised bump — absent reads as []. Authored
   *  only in the timeline logger; the card logger never sets it. Persisted as
   *  WorkoutNote rows at save (useSaveWorkout flush), never on the set path. */
  noteEvents?: NoteEventDraft[];
}

// The reducer action union moved to loggerActions.ts (2026-06-03) to keep
// this file — the draft data shapes — under the 500-line budget. Re-exported
// here so existing `import { LoggerAction } from './loggerDraftTypes'`
// callsites keep working.
export type { LoggerAction } from './loggerActions';
